import { setOpeningSketchCustomTypeOptions } from '@pointorama/potree-shared';
import { customSketchObjectTypeId, parseObjectDefinitionSpec, type ObjectDefinitionSpec } from 'shared-types';
import { buildCustomSketchObjectType } from '../../../potree/sketchObjects/custom/customSketchObjectType';
import { getCustomOpeningTypes, offeredCustomOpeningTypes, setCustomOpeningTypes, type CustomOpeningType } from '../../../potree/sketchObjects/custom/openings/customOpeningRegistry';
import {
  batchSketchObjectTypeChanges,
  getAllSketchObjectTypes,
  setSketchObjectLibraryConfig,
  unregisterSketchObjectType,
  upsertSketchObjectType,
} from '../../../potree/sketchObjects/registry';
import { ObjectDefinitionAssetKind, ObjectDefinitionStatus, type ObjectDefinitionFieldsFragment, type ObjectLibraryFieldsFragment } from '../../../types/graphqlTypes';

/** Which spec of a definition a view renders: projects the published one, the editor/library page the draft. */
export type ObjectLibrarySpecSource = 'published' | 'draft';

/** The typed spec a definition renders from `source`, or null when there is none (or it fails validation). */
export function definitionSpec(definition: Pick<ObjectDefinitionFieldsFragment, 'draftSpec' | 'publishedSpec'>, source: ObjectLibrarySpecSource): ObjectDefinitionSpec | null {
  const raw: unknown = source === 'draft' ? (definition.draftSpec ?? definition.publishedSpec) : definition.publishedSpec;
  if (raw === null || raw === undefined) return null;
  const parsed = parseObjectDefinitionSpec(raw);
  return parsed.ok ? parsed.spec : null;
}

/** Signature of what a registered custom type was built from — unchanged definitions are not re-registered. */
const registeredSignatures = new Map<string, string>();

/**
 * Make the library's definitions sketch-object types (replacing whatever a
 * previous library registered) and apply its configuration — one registry
 * notification for the whole library.
 */
export function applyObjectLibrary(library: ObjectLibraryFieldsFragment, source: ObjectLibrarySpecSource = 'published'): void {
  batchSketchObjectTypeChanges(() => {
    const wanted = new Set<string>();
    const openings: CustomOpeningType[] = [];
    for (const definition of library.definitions) {
      const spec = definitionSpec(definition, source);
      if (!spec) continue;
      const listed = definition.status !== ObjectDefinitionStatus.Archived;
      // Doors / windows are opening symbols, not placeable objects.
      if (spec.opening) {
        openings.push({ id: customSketchObjectTypeId(definition.id), definitionId: definition.id, label: definition.name, kind: spec.opening.kind, spec, listed });
        continue;
      }
      wanted.add(definition.typeId);
      const signature = JSON.stringify([definition.name, definition.categoryId, definition.tags, listed, spec]);
      if (registeredSignatures.get(definition.typeId) === signature) continue;
      registeredSignatures.set(definition.typeId, signature);
      // The model URL (a fresh read SAS each fetch) is not part of the signature: a new SAS alone doesn't re-register.
      const modelUrl = spec.model3d ? definition.assets.find((a) => a.id === spec.model3d?.assetId && a.kind === ObjectDefinitionAssetKind.ModelGlb)?.url : undefined;
      upsertSketchObjectType(buildCustomSketchObjectType({ definitionId: definition.id, name: definition.name, categoryId: definition.categoryId, spec, tags: definition.tags, listed, modelUrl }));
    }
    for (const type of getAllSketchObjectTypes()) {
      if (type.custom && !wanted.has(type.id)) {
        unregisterSketchObjectType(type.id);
        registeredSignatures.delete(type.id);
      }
    }
    setSketchObjectLibraryConfig(library.config);
    setCustomOpeningTypes(openings);
    applyOpeningTypeOptions();
  });
}

/** The organisation's doors / windows in the door / window type dropdowns (hidden ones left out, archived kept for display). */
function applyOpeningTypeOptions(): void {
  const options = (kind: 'DOOR' | 'WINDOW') => {
    const offered = offeredCustomOpeningTypes(kind);
    const rest = getCustomOpeningTypes(kind).filter((t) => !offered.includes(t));
    return [...offered.map((t) => ({ value: t.id, label: t.label })), ...rest.map((t) => ({ value: t.id, label: t.label, archived: true }))];
  };
  setOpeningSketchCustomTypeOptions({ doors: options('DOOR'), windows: options('WINDOW') });
}

/** Drop every custom type and the organisation configuration (leaving a project). */
export function clearObjectLibrary(): void {
  batchSketchObjectTypeChanges(() => {
    for (const type of getAllSketchObjectTypes()) if (type.custom) unregisterSketchObjectType(type.id);
    registeredSignatures.clear();
    setSketchObjectLibraryConfig(null);
    setCustomOpeningTypes([]);
    applyOpeningTypeOptions();
  });
}
